import dayjs from 'dayjs';
import weekOfYear from "dayjs/plugin/weekOfYear";
import updateLocale from "dayjs/plugin/updateLocale";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useRef, useState, useEffect } from 'react';

import './WeeklyCalendar.css';

// 扩展dayjs
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);

// 设置周一为一周的起始日
dayjs.updateLocale('zh-cn', {
  weekStart: 1 // 周一
});

// 左右箭头图标
const LeftArrow = () => (
  <svg viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8c-16.4 12.8-16.4 37.5 0 50.3l450.8 352.1c5.3 4.1 12.9 0.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
  </svg>
);

const RightArrow = () => (
  <svg viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7c-5.3-4.1-12.9-0.4-12.9 6.3v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.8 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1c16.4-12.8 16.4-37.6 0-50.4z" />
  </svg>
);

interface WeekData {
  sequence: number;
  weeklySequence: number;
  startDate: string;
  endDate: string;
}

interface CalendarData {
  [year: string]: WeekData[];
}

interface WeeklyCalendarProps {
  calendarData: CalendarData | null;
  selectedDate: dayjs.Dayjs | null;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  onChange: (date: dayjs.Dayjs) => void;
  onClose: () => void;
}

// 获取日期所在的周数据
const getWeekData = (date: dayjs.Dayjs | null, calendarData: CalendarData | null): WeekData | null => {
  if (!calendarData || !date) return null;
  
  const year = date.year().toString();
  const yearData = calendarData[year];
  
  if (!yearData) return null;
  
  return yearData.find(week => {
    const startDate = dayjs(week.startDate);
    const endDate = dayjs(week.endDate);
    return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
  }) || null;
};

// 检查日期是否在有效的周范围内
const isDateInValidWeek = (date: dayjs.Dayjs, calendarData: CalendarData | null): boolean => !!getWeekData(date, calendarData);

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  calendarData,
  selectedDate,
  minDate,
  maxDate,
  onChange,
  onClose
}) => {
  const [currentMonth, setCurrentMonth] = useState<dayjs.Dayjs>(selectedDate || dayjs());
  const [hoveredWeek, setHoveredWeek] = useState<WeekData | null>(null);
  const [calendarPosition, setCalendarPosition] = useState<{top?: string, bottom?: string, left?: string, right?: string, transform?: string}>({});
  const calendarRef = useRef<HTMLDivElement>(null);

  // 处理点击外部关闭日历
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 如果点击事件不是来自日历元素本身或其子元素
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        // 额外检查点击的元素是否是输入框或其子元素
        const isClickOnInput = event.target instanceof Element && 
          (event.target.closest('.custom-weekly-input') || 
           event.target.closest('.weekly-picker-input'));
        
        // 只有当点击不是在输入框或日历上时才关闭日历
        if (!isClickOnInput) {
          onClose();
        }
      }
    };

    // 添加延迟，避免刚打开日历就被立即关闭
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // 计算并更新日历位置
  useEffect(() => {
    if (calendarRef.current) {
      const calendarEl = calendarRef.current;
      
      // 获取与日历相关联的输入框元素
      const inputEl = calendarEl.closest('.custom-weekly-input');
      if (!inputEl) return;
      
      // 获取输入框的位置信息
      const inputRect = inputEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // 计算日历的最佳位置
      const position: {top?: number, bottom?: string, left?: number, right?: string} = {};
      
      // 垂直位置计算 - 优先显示在输入框下方
      if (inputRect.bottom + 300 > viewportHeight) {
        // 如果下方空间不足，显示在上方
        position.bottom = `${window.innerHeight - inputRect.top + 2}px`;
      } else {
        position.top = inputRect.bottom + 2; // 输入框底部位置 + 2px间距
      }
      
      // 水平位置计算
      const spaceForRight = viewportWidth - inputRect.left - 280; // 280px是日历宽度
      if (spaceForRight < 0) {
        // 右侧空间不足，尝试左对齐
        position.left = Math.max(10, inputRect.left); // 至少保留10px的左边距
      } else {
        // 有足够空间，左对齐
        position.left = inputRect.left;
      }
      
      // 应用位置样式
      calendarEl.style.top = position.top ? `${position.top}px` : 'auto';
      calendarEl.style.bottom = position.bottom || 'auto';
      calendarEl.style.left = position.left ? `${position.left}px` : 'auto';
      calendarEl.style.right = position.right || 'auto';
    }
  }, []);

  // 生成日历头部 (周几)
  const renderWeekDays = () => {
    const weekDays = ['一', '二', '三', '四', '五', '六', '日'];
    
    return (
      <div className="week-days">
        {weekDays.map((day, index) => (
          <div key={index} className="week-day">
            {day}
          </div>
        ))}
      </div>
    );
  };

  // 获取月份的天数
  const getDaysInMonth = (date: dayjs.Dayjs) => {
    const firstDay = date.startOf('month');
    const daysInMonth = date.daysInMonth();
    const startOfWeek = firstDay.day(); // 获取月初是周几 (0-6, 0是周日)
    
    // 调整到周一为一周的第一天
    // 如果是周日(0)，则应该显示为一周的第7天，所以调整为6
    // 如果是周一到周六(1-6)，则应该显示为一周的第1-6天，所以调整为比原来少1的天数
    const adjustedStartDay = startOfWeek === 0 ? 6 : startOfWeek - 1;
    
    // 前一个月需要显示的天数
    const prevMonthDays = adjustedStartDay;
    
    // 生成日期数组，包括上月、当月和下月的日期
    const days: dayjs.Dayjs[] = [];
    
    // 添加上月的日期
    for (let i = prevMonthDays - 1; i >= 0; i -= 1) {
      days.push(firstDay.subtract(i + 1, 'day'));
    }
    
    // 添加当月的日期
    for (let i = 0; i < daysInMonth; i += 1) {
      days.push(firstDay.add(i, 'day'));
    }
    
    // 计算还需要多少天来填充最后一周
    const remainingDays = (7 - (days.length % 7)) % 7;
    
    // 添加下月的日期
    for (let i = 1; i <= remainingDays; i += 1) {
      days.push(firstDay.add(daysInMonth - 1 + i, 'day'));
    }
    
    return days;
  };

  // 处理日期点击
  const handleDateClick = (date: dayjs.Dayjs) => {
    if (!calendarData) return;
    
    const weekData = getWeekData(date, calendarData);
    if (weekData) {
      const startDate = dayjs(weekData.startDate);
      onChange(startDate);
      onClose();
    }
  };

  // 处理日期悬停
  const handleDateHover = (date: dayjs.Dayjs) => {
    if (!calendarData) return;
    
    const weekData = getWeekData(date, calendarData);
    setHoveredWeek(weekData);
  };

  // 处理月份切换
  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  // 判断日期是否被禁用
  const isDateDisabled = (date: dayjs.Dayjs): boolean => {
    // 检查日期是否在有效周范围内
    if (!isDateInValidWeek(date, calendarData)) {
      return true;
    }
    
    // 检查最小和最大日期限制
    if (minDate && date.isBefore(minDate, 'day')) {
      return true;
    }
    
    if (maxDate && date.isAfter(maxDate, 'day')) {
      return true;
    }
    
    return false;
  };

  // 判断日期是否在当前选中的周内
  const isDateInSelectedWeek = (date: dayjs.Dayjs): boolean => {
    if (!selectedDate || !calendarData) return false;
    
    const selectedWeekData = getWeekData(selectedDate, calendarData);
    if (!selectedWeekData) return false;
    
    const startDate = dayjs(selectedWeekData.startDate);
    const endDate = dayjs(selectedWeekData.endDate);
    
    return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
  };

  // 判断日期是否在悬停的周内
  const isDateInHoveredWeek = (date: dayjs.Dayjs): boolean => {
    if (!hoveredWeek) return false;
    
    const startDate = dayjs(hoveredWeek.startDate);
    const endDate = dayjs(hoveredWeek.endDate);
    
    return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
  };

  // 获取日期类名
  const getDateClassName = (date: dayjs.Dayjs) => {
    const classNames = ['calendar-day'];
    
    // 判断是否是当前月份的日期
    if (date.month() !== currentMonth.month()) {
      classNames.push('other-month');
    }
    
    // 判断是否是今天
    if (date.isSame(dayjs(), 'day')) {
      classNames.push('today');
    }
    
    // 判断是否被禁用
    if (isDateDisabled(date)) {
      classNames.push('disabled');
    } else if (isDateInSelectedWeek(date)) {
      // 判断是否在选中的周内
      classNames.push('selected');
      
      // 判断是否是周的开始和结束日期
      if (selectedDate && calendarData) {
        const selectedWeekData = getWeekData(selectedDate, calendarData);
        if (selectedWeekData) {
          if (date.isSame(dayjs(selectedWeekData.startDate), 'day')) {
            classNames.push('week-start');
          }
          if (date.isSame(dayjs(selectedWeekData.endDate), 'day')) {
            classNames.push('week-end');
          }
        }
      }
    } else if (isDateInHoveredWeek(date)) {
      // 判断是否在悬停的周内（且不在已选中的周内）
      classNames.push('hovered');
      
      // 判断是否是周的开始和结束日期
      if (hoveredWeek) {
        if (date.isSame(dayjs(hoveredWeek.startDate), 'day')) {
          classNames.push('week-start');
        }
        if (date.isSame(dayjs(hoveredWeek.endDate), 'day')) {
          classNames.push('week-end');
        }
      }
    }
    
    return classNames.join(' ');
  };

  // 渲染日历
  const renderCalendar = () => {
    const days = getDaysInMonth(currentMonth);
    const weeks = [];
    
    // 分割成每周
    for (let i = 0; i < days.length; i += 7) {
      const weekDays = days.slice(i, i + 7);
      weeks.push(weekDays);
    }
    
    return (
      <div className="calendar-weeks">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="calendar-week">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={getDateClassName(day)}
                onClick={() => !isDateDisabled(day) && handleDateClick(day)}
                onMouseEnter={() => !isDateDisabled(day) && handleDateHover(day)}
                onMouseLeave={() => setHoveredWeek(null)}
                role="button"
                tabIndex={isDateDisabled(day) ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (!isDateDisabled(day)) {
                      handleDateClick(day);
                    }
                  }
                }}
              >
                {day.date()}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // 获取周信息文本
  const getWeekInfo = () => {
    if (!selectedDate || !calendarData) return '';
    
    const weekData = getWeekData(selectedDate, calendarData);
    if (!weekData) return '';
    
    const startDate = dayjs(weekData.startDate);
    const endDate = dayjs(weekData.endDate);
    
    // return `${startDate.year()}年${startDate.month() + 1}月第${weekData.weeklySequence}周 (${startDate.format('MM/DD')}-${endDate.format('MM/DD')})`;
    return `${startDate.year()}年${startDate.month() + 1}月第${weekData.weeklySequence}周`;
  };

  // 计算日历样式，基于我们检测到的位置
  const calendarStyle = {
    ...calendarPosition
  };

  return (
    <div className="weekly-calendar" ref={calendarRef} style={calendarStyle}>
      <div className="calendar-header">
        <button 
          className="prev-month" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePrevMonth();
          }} 
          type="button"
        >
          <LeftArrow />
        </button>
        <div className="current-month">
          {currentMonth.year()}年{currentMonth.month() + 1}月
        </div>
        <button 
          className="next-month" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleNextMonth();
          }} 
          type="button"
        >
          <RightArrow />
        </button>
      </div>
      
      {renderWeekDays()}
      {renderCalendar()}
      
      {/* {selectedDate && (
        <div className="selected-week-info">
          {getWeekInfo()}
        </div>
      )} */}
    </div>
  );
};

export default WeeklyCalendar; 